import { COMMA, ENTER, V } from '@angular/cdk/keycodes';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators, ValidatorFn, AbstractControl } from '@angular/forms';
import { MatChipInputEvent } from '@angular/material/chips';
import { Store } from '@ngrx/store';
import { combineLatest, map, Observable, startWith, Subject, takeUntil } from 'rxjs';
import { AppState } from 'src/app/core/app.state';
import { CommissionType, INewCommissionDTO } from 'src/app/features/agents/models/commissions.models';
import { CommissionService } from 'src/app/features/agents/services/commission.service';
import { IBrand } from 'src/app/features/brands/models/brands.models';
import { ICategory } from 'src/app/features/categories/models/categories.models';
import { ITag } from 'src/app/features/tags/models/tags.models';
import { atLeastOneValidator } from 'src/app/shared/validators/fields-validators';

@Component({
  selector: 'app-new-commission-details',
  templateUrl: './new-commission-details.component.html',
  styleUrls: ['./new-commission-details.component.scss'],
})
export class NewCommissionDetailsComponent implements OnInit {
  @Input() commission!: INewCommissionDTO;
  @Output() nextStep = new EventEmitter<Partial<INewCommissionDTO>>();

  detailsForm!: FormGroup;
  baseCommissionTypeControls!: any;
  brandCommissionTypeControls!: any;

  commissionType!: CommissionType;

  commissionCalculationTypes: any[] = [];

  hasExpirationDate = false;

  brands$: Observable<IBrand[]> = this.store.select((state: AppState) => state.brands.brands);
  labels$: Observable<ITag[]> = this.store.select((state: AppState) => state.tags.tags);
  categories$: Observable<ICategory[]> = this.store.select((state: AppState) => state.categories.categories);

  brands: IBrand[] = [];
  labels: ITag[] = [];
  categories: ICategory[] = [];

  filteredBrands!: Observable<IBrand[]>;
  filteredLabels!: Observable<ITag[]>;
  filteredCategories!: Observable<ICategory[]>;

  brandControl = new FormControl('');
  labelControl = new FormControl('');
  categoryControl = new FormControl('');

  selectedBrands: IBrand[] = [];
  selectedLabels: ITag[] = [];
  selectedCategories: ICategory[] = [];

  destroy$ = new Subject<void>();
  separatorKeysCodes: number[] = [ENTER, COMMA];

  constructor(private store: Store<AppState>, private commissionService: CommissionService) {}

  ngOnInit(): void {
    this.initControls();

    this.commissionCalculationTypes = this.commissionService.getCommissionCalculationTypes();

    this.detailsForm = new FormGroup({ ...this.baseCommissionTypeControls });

    // Set Already Inputed Data
    this.commissionType = this.commission.commission_type;

    if (this.commissionType === 'brand') {
      this.detailsForm = new FormGroup({ ...this.brandCommissionTypeControls });
    }

    if (this.commission.expiry_date) {
      this.hasExpirationDate = true;
    }

    // Filter brands, labels and categories based on the input value
    this.filteredBrands = combineLatest([this.brandControl.valueChanges.pipe(startWith('')), this.brands$.pipe(takeUntil(this.destroy$))]).pipe(
      map(([value, brands]) => {
        this.brands = brands.filter((brand) => brand.name && brand.name.trim() !== '');

        // Set already selected brands
        if (this.commission.brand_ids.length) {
          this.selectedBrands = this.brands.filter((b) => this.commission.brand_ids.includes(b.id));
        }

        return this.filterSelectableItems(value || '', this.brands, this.selectedBrands);
      })
    );

    this.filteredLabels = combineLatest([this.labelControl.valueChanges.pipe(startWith('')), this.labels$.pipe(takeUntil(this.destroy$))]).pipe(
      map(([value, labels]) => {
        this.labels = labels.filter((label) => label.name && label.name.trim() !== '');

        // Set already selected labels
        if (this.commission.label_ids.length) {
          this.selectedLabels = this.labels.filter((l) => this.commission.label_ids.includes(l.id));
        }

        return this.filterSelectableItems(value || '', this.labels, this.selectedLabels);
      })
    );

    this.filteredCategories = combineLatest([
      this.categoryControl.valueChanges.pipe(startWith('')),
      this.categories$.pipe(takeUntil(this.destroy$)),
    ]).pipe(
      map(([value, categories]) => {
        this.categories = categories.filter((category) => category.name && category.name.trim() !== '');

        // Set already selected categories
        if (this.commission.category_ids.length) {
          this.selectedCategories = this.categories.filter((c) => this.commission.category_ids.includes(c.id));
        }

        return this.filterSelectableItems(value || '', this.categories, this.selectedCategories);
      })
    );
  }

  initControls() {
    this.baseCommissionTypeControls = {
      name: new FormControl(this.commission.name, [Validators.required]),
      description: new FormControl(this.commission.description),
      commission: new FormControl(this.commission.commission, [Validators.required, Validators.min(0), Validators.max(100)]),
      calculation_method: new FormControl(
        {
          value: this.commission.calculation_method,
          disabled: true,
        },
        [Validators.required]
      ),
      expiry_date: new FormControl(this.commission.expiry_date),
    };

    this.brandCommissionTypeControls = {
      ...this.baseCommissionTypeControls,
      brand_ids: new FormControl(this.commission.brand_ids, [Validators.required, atLeastOneValidator()]),
      label_ids: new FormControl(this.commission.label_ids),
      category_ids: new FormControl(this.commission.category_ids),
    };
  }

  onInputRemoveLeadingZero(event: Event, formGroup: FormGroup, formControlName: string) {
    const input = event.target as HTMLInputElement;
    if (/^0[0-9]+/.test(input.value)) {
      input.value = input.value.replace(/^0+/, '');
      formGroup.get(formControlName)?.setValue(input.value);
    }
  }

  toggleCommissionType(type: CommissionType) {
    this.commissionType = type;

    if (type === 'base') {
      this.detailsForm = new FormGroup({ ...this.baseCommissionTypeControls });
    } else {
      this.detailsForm = new FormGroup({ ...this.brandCommissionTypeControls });
    }
  }

  toggleExpirationDate() {
    this.hasExpirationDate = !this.hasExpirationDate;

    if (this.hasExpirationDate) {
      this.detailsForm.get('expiry_date')?.setValidators([Validators.required]);
    } else {
      this.detailsForm.get('expiry_date')?.clearValidators();
      this.detailsForm.get('expiry_date')?.setValue(null);
      // Reset the touched and pristine states to hide validation messages
      this.detailsForm.get('expiry_date')?.markAsUntouched();
      this.detailsForm.get('expiry_date')?.markAsPristine();
    }
  }

  // Select a brand
  selectBrand(brand: IBrand) {
    if (!this.selectedBrands.some((b) => b.id === brand.id)) {
      this.selectedBrands.push(brand);
    }

    console.log('Brands', this.selectedBrands, brand);
    this.brandControl.setValue('');
    this.detailsForm.get('brand_ids')?.setValue(this.selectedBrands.map((brand: IBrand) => brand.id));
  }

  // Remove a brand
  removeBrand(brand: IBrand) {
    this.selectedBrands = this.selectedBrands.filter((b) => b.id !== brand.id);
    // Trigger input change to refresh filtered list
    this.brandControl.setValue(this.brandControl.value);
    this.detailsForm.get('brand_ids')?.setValue(this.selectedBrands.map((brand: IBrand) => brand.id));
  }

  // Add a brand from the input
  addBrandFromInput(event: MatChipInputEvent) {
    const input = event.input;
    const value = event.value?.trim();

    if (value) {
      const match = this.brands.find((b) => b.name.toLowerCase() === value.toLowerCase());
      if (match && !this.selectedBrands.some((b) => b.id === match.id)) {
        this.selectedBrands.push(match);
      }
    }
    if (input) {
      input.value = '';
    }
    this.brandControl.setValue('');
    this.detailsForm.get('brand_ids')?.setValue(this.selectedBrands.map((brand: IBrand) => brand.id));
  }

  // Select a label
  selectLabel(label: any) {
    if (!this.selectedLabels.some((l) => l.id === label.id)) {
      this.selectedLabels.push(label);
    }
    this.labelControl.setValue('');
    this.detailsForm.get('label_ids')?.setValue(this.selectedLabels.map((label: ITag) => label.id));
  }

  // Remove a label
  removeLabel(label: any) {
    this.selectedLabels = this.selectedLabels.filter((l) => l.id !== label.id);
    // Trigger input change to refresh filtered list
    this.labelControl.setValue(this.labelControl.value);
    this.detailsForm.get('label_ids')?.setValue(this.selectedLabels.map((label: ITag) => label.id));
  }

  // Add a label from the input
  addLabelFromInput(event: MatChipInputEvent) {
    const input = event.input;
    const value = event.value?.trim();
    if (value) {
      const match = this.labels.find((l) => l.name.toLowerCase() === value.toLowerCase());
      if (match && !this.selectedLabels.some((l) => l.id === match.id)) {
        this.selectedLabels.push(match);
      }
    }
    if (input) {
      input.value = '';
    }
    this.labelControl.setValue('');
    this.detailsForm.get('label_ids')?.setValue(this.selectedLabels.map((label: ITag) => label.id));
  }

  // Select a category
  selectCategory(category: any) {
    if (!this.selectedCategories.some((c) => c.id === category.id)) {
      this.selectedCategories.push(category);
    }
    this.categoryControl.setValue('');
    this.detailsForm.get('category_ids')?.setValue(this.selectedCategories.map((category: ICategory) => category.id));
  }

  // Remove a category
  removeCategory(category: any) {
    this.selectedCategories = this.selectedCategories.filter((c) => c.id !== category.id);
    // Trigger input change to refresh filtered list
    this.categoryControl.setValue(this.categoryControl.value);
    this.detailsForm.get('category_ids')?.setValue(this.selectedCategories.map((category: ICategory) => category.id));
  }

  // Add a category from the input
  addCategoryFromInput(event: MatChipInputEvent) {
    const input = event.input;
    const value = event.value?.trim();
    if (value) {
      const match = this.categories.find((c) => c.name.toLowerCase() === value.toLowerCase());
      if (match && !this.selectedCategories.some((c) => c.id === match.id)) {
        this.selectedCategories.push(match);
      }
    }
    if (input) {
      input.value = '';
    }
    this.categoryControl.setValue('');
    this.detailsForm.get('category_ids')?.setValue(this.selectedCategories.map((category: ICategory) => category.id));
  }

  /**
   * Filters items for use in chips+autocomplete multiselects.
   * Removes items with empty name, removes already selected, and filters by input value if string.
   */
  private filterSelectableItems<T extends { id: number; name: string }>(value: string | T, items: T[], selected: T[]): T[] {
    // Remove items with empty name
    const validItems = items.filter((item) => item.name && item.name.trim() !== '');
    // Remove already selected items
    let availableItems = validItems.filter((item) => !selected.some((sel) => sel.id === item.id));
    // Only filter by name if value is a string
    if (typeof value === 'string') {
      const filterValue = value.toLowerCase();
      return availableItems.filter((item) => item.name.toLowerCase().includes(filterValue));
    }
    // If value is an object (after selection), show all available items
    return availableItems;
  }

  next() {
    if (!this.detailsForm.valid) {
      this.detailsForm.markAllAsTouched();
      return;
    }

    const stepPayload = this.detailsForm.getRawValue();

    if (stepPayload.expiry_date) {
      stepPayload.expiry_date = new Date(stepPayload.expiry_date).toISOString().split('T')[0];
    }

    console.log('Details', stepPayload);
    this.nextStep.emit({ ...stepPayload, commission_type: this.commissionType });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
